/**
 * TreeMapChildrenGrid Component
 *
 * Displays children of a parent node in a compact grid:
 * - Max 5 cards vertically
 * - Expands horizontally up to 3 columns wide
 * - Scrollable if more than 15 cards
 */

import React from 'react';
import { useTranslation } from 'localization';
import type { GridNode } from '../../organisms/TreeMap/useTreeMapLayout';
import type { TreeMapTemplate, TemplateLevelDefinition, BasicSphereInfo } from '../../organisms/TreeMap/types';
import { resolveLabel } from '../../../utils/resolveLabel';
import { resolveLevelConfig, ROOT_COLUMN_LEVEL_ID } from '../../organisms/TreeMap/defaults';
import type { BaseViewConfig, TreeMapViewConfig } from 'types/contextView';
import TreeMapGridNode from '../TreeMapGridNode/TreeMapGridNode';
import './TreeMapChildrenGrid.scss';

export interface TreeMapChildrenGridProps {
  parentNode: GridNode;
  childNodes: GridNode[];
  levelConfig?: TemplateLevelDefinition;
  template: TreeMapTemplate;
  editable?: boolean;
  /** Merged base display config; resolved per-column via `resolveLevelConfig` using `levelConfig.id`. */
  baseConfig: BaseViewConfig;
  /** Merged view config; resolved per-column via `resolveLevelConfig` using `levelConfig.id`. */
  viewConfig: TreeMapViewConfig;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  collapsedNodeIds: Set<string>;
  paginationState: Record<string, { nextToken?: string; hasMore: boolean; loading: boolean }>;
  newSphereIds?: Set<string>;
  onNodeSelect?: (sphereId: string) => void;
  onNodeExpand?: (sphereId: string) => void;
  onNodeCreate?: (node: any) => void;
  onNodeUpdate?: (node: any) => void;
  onNodeDelete?: (sphereId: string) => void;
  onToggleCollapse?: (nodeId: string) => void;
  onLoadMore?: () => void;
  onFocus?: (sphereId: string) => void;
  schemas?: any[];
  sphereLookup?: Record<string, BasicSphereInfo>;
}

const MAX_ROWS = 5;
const MAX_COLS = 3;

const TreeMapChildrenGrid: React.FC<TreeMapChildrenGridProps> = ({
  parentNode,
  childNodes,
  levelConfig,
  template,
  editable = false,
  baseConfig,
  viewConfig,
  hasMore = false,
  isLoadingMore = false,
  collapsedNodeIds,
  paginationState,
  newSphereIds = new Set(),
  onNodeSelect,
  onNodeExpand,
  onNodeCreate,
  onNodeUpdate,
  onNodeDelete,
  onToggleCollapse,
  onLoadMore,
  onFocus,
  schemas,
  sphereLookup,
}) => {
  const { t, i18n } = useTranslation();
  const childTypeName = resolveLabel(levelConfig?.label, i18n.language) || t('common.item');
  const resolvedConfig = resolveLevelConfig({ base: baseConfig, view: viewConfig }, levelConfig?.id ?? ROOT_COLUMN_LEVEL_ID);

  return (
    <div className="tree-map-children-grid-container">
      {/* Children Grid - always show if there are children OR if editable (to show add card) */}
      {(childNodes.length > 0 || (editable && onNodeCreate)) && (
        <div className="tree-map-children-grid">
          {childNodes.map((node) => {
            const isCollapsed = collapsedNodeIds.has(node.sphere.id);
            const hasChildren = node.children.length > 0;

            return (
              <div key={node.sphere.id} className="tree-map-children-grid__card">
                <TreeMapGridNode
                  node={{
                    ...node,
                    rowStart: 1,
                    rowSpan: 1,
                  }}
                  levelConfig={levelConfig}
                  template={template}
                  editable={editable}
                  baseConfig={baseConfig}
                  viewConfig={viewConfig}
                  columnOffset={0} // No offset - not in main grid
                  hasMore={paginationState[node.sphere.id]?.hasMore || false}
                  isLoadingMore={paginationState[node.sphere.id]?.loading || false}
                  isCollapsed={isCollapsed}
                  hasChildren={hasChildren}
                  isNew={newSphereIds.has(node.sphere.id)}
                  onSelect={() => onNodeSelect?.(node.sphere.id)}
                  onExpand={() => onNodeExpand?.(node.sphere.id)}
                  onCreate={() => onNodeCreate?.(node.sphere)}
                  onUpdate={() => onNodeUpdate?.(node.sphere)}
                  onDelete={() => onNodeDelete?.(node.sphere.id)}
                  onToggleCollapse={() => onToggleCollapse?.(node.sphere.id)}
                  onLoadMore={() => onLoadMore?.()}
                  onFocus={() => onFocus?.(node.sphere.id)}
                  schemas={schemas}
                  sphereLookup={sphereLookup}
                />
              </div>
            );
          })}

          {/* Add Card - styled differently to indicate it's not a real card */}
          {/* Height matches the current cardView detail level */}
          {editable && onNodeCreate && (() => {
            // Height should match actual card heights for each detail level
            const ADD_CARD_HEIGHT = {
              minimal: 50,
              standard: 160,
              expanded: 280,
            };
            const height = ADD_CARD_HEIGHT[resolvedConfig.cardDensity] || 120;

            return (
              <button
                type="button"
                key="add-card"
                className={`tree-map-children-grid__card tree-map-children-grid__add-card tree-map-children-grid__add-card--${resolvedConfig.cardDensity}`}
                style={{ height: `${height}px`, minHeight: `${height}px` }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Pass sphere with column information so TreeMap can get the right filters
                  onNodeCreate({ ...parentNode.sphere, __columnIndex: parentNode.column });
                }}
              >
                <div className="tree-map-children-grid__add-card-content">
                  <i className="pi pi-plus" />
                  <span>{t('treeMap.grid.addItem', { label: childTypeName })}</span>
                </div>
              </button>
            );
          })()}
        </div>
      )}

      {/* Empty State - only show when not editable and no children */}
      {childNodes.length === 0 && !editable && (
        <div className="tree-map-children-grid__empty">
          <span style={{ color: '#999', fontSize: '13px' }}>{t('treeMap.grid.noItems', { label: childTypeName.toLowerCase() })}</span>
        </div>
      )}

      {/* Footer with Load More button - only show when there are children and more to load */}
      {childNodes.length > 0 && hasMore && (
        <div className="tree-map-children-grid__footer">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLoadMore?.();
            }}
            disabled={isLoadingMore}
            style={{
              background: 'none',
              border: 'none',
              color: isLoadingMore ? '#999' : '#377fc4',
              cursor: isLoadingMore ? 'wait' : 'pointer',
              fontSize: '13px',
              padding: '0',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <i className={`pi ${isLoadingMore ? 'pi-spinner pi-spin' : 'pi-angle-down'}`} style={{ fontSize: '12px' }} />
            <span>{isLoadingMore ? t('common.loading') : t('common.loadMore')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default TreeMapChildrenGrid;
